import { useTranslation } from "react-i18next";

import { Grid, InputAdornment } from "@mui/material";
import { FormikProps, FormikValues } from "formik";

import { FormikDecimalTextField } from "../../../../../../formik/FormikDecimalTextField";
import { FieldPath } from "../../../../../../formik/FormikField.types";
import { FormikTextField } from "../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../formik/FormikYesNoAutocomplete";
import { FormikDatePicker } from "../../../../../../formik/formik-date-picker/FormikDatePicker";
import { calculateAge } from "../../../../../../utils/calculateAge";
import { isValidDate } from "../../../../../../utils/isValidDate";
import { FieldWithInfo } from "../../../personal-data/components/field-with-info/FieldWithInfo";
import { DependantFormValue, DependantMaxLength } from "../../RelativesView.types";
import { MaintenanceEntitlementInfo } from "../maintenance-entitlement-info/MaintenanceEntitlementInfo";
import { NotFullyPaidWarning } from "../not-fully-paid-warning/NotFullyPaidWarning";
import { RelationshipSelect } from "../relationship-select/RelationshipSelect";

interface Props<T extends FormikValues> {
  formik: FormikProps<T>;
  dependant: DependantFormValue;
  base?: string;
}

export function DependantFields<T extends FormikValues>({ formik, dependant, base }: Props<T>) {
  const { t } = useTranslation();
  const section = "clients.client.debtSettlement.assetInventory.dependants";
  const firstNameLabel = t(`${section}.firstName`);

  const fieldPath = (field: keyof DependantFormValue): FieldPath<T> => (base ? `${base}.${field}` : field);

  const isEntitled = dependant.isEntitledToMaintenance === true;
  const hasToPay = isEntitled && dependant.hasToPayAlimony === true;
  const paidFull = dependant.paidFullLastMonth;

  const birthDate = dependant.birthDate;
  const age = isValidDate(birthDate) ? calculateAge(birthDate, new Date()) : null;
  const birthDateLabel =
    age !== null ? `${t(`${section}.birthDate`)} ${t(`${section}.age`, { count: age })}` : t(`${section}.birthDate`);

  return (
    <>
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={5}>
          <FieldWithInfo fieldLabel={firstNameLabel} info={t(`${section}.firstNameInfo`)}>
            <FormikTextField
              formik={formik}
              path={fieldPath("firstName")}
              label={firstNameLabel}
              slotProps={{ input: { inputProps: { maxLength: DependantMaxLength.firstName } } }}
            />
          </FieldWithInfo>
        </Grid>
        <Grid size={3}>
          <RelationshipSelect
            formik={formik}
            path={base ? `${base}.relationship` : "relationship"}
            label={t(`${section}.relationship`)}
          />
        </Grid>
        <Grid size={4}>
          <FormikDatePicker formik={formik} path={fieldPath("birthDate")} label={birthDateLabel} clearable />
        </Grid>

        <Grid size={6}>
          <FormikYesNoAutocomplete
            formik={formik}
            path={fieldPath("livesInHousehold")}
            label={t(`${section}.livesInHousehold`)}
          />
        </Grid>
        <Grid size={6} />

        <Grid size={6}>
          <FormikYesNoAutocomplete
            formik={formik}
            path={fieldPath("isJointlyLiable")}
            label={t(`${section}.isJointlyLiable`)}
          />
        </Grid>
        <Grid size={6}>
          <FieldWithInfo
            fieldLabel={t(`${section}.isEntitledToMaintenance`)}
            infoContent={<MaintenanceEntitlementInfo />}
          >
            <FormikYesNoAutocomplete
              formik={formik}
              path={fieldPath("isEntitledToMaintenance")}
              label={t(`${section}.isEntitledToMaintenance`)}
            />
          </FieldWithInfo>
        </Grid>

        {isEntitled && (
          <Grid size={6}>
            <FormikYesNoAutocomplete
              formik={formik}
              path={fieldPath("hasToPayAlimony")}
              label={t(`${section}.hasToPayAlimony`)}
            />
          </Grid>
        )}
        {hasToPay && (
          <Grid size={6}>
            <FormikDecimalTextField
              formik={formik}
              path={fieldPath("monthlyAlimonyAmount")}
              label={t(`${section}.monthlyAlimonyAmount`)}
              decimalScale={2}
              minValue={0}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                },
              }}
            />
          </Grid>
        )}
        {hasToPay && (
          <Grid size={12}>
            <FormikYesNoAutocomplete
              formik={formik}
              path={fieldPath("paidFullLastMonth")}
              label={t(`${section}.paidFullLastMonth`)}
            />
          </Grid>
        )}
        {hasToPay && paidFull === false && (
          <Grid size={12}>
            <FormikYesNoAutocomplete
              formik={formik}
              path={fieldPath("fullyGarnishedLastMonth")}
              label={t(`${section}.fullyGarnishedLastMonth`)}
            />
          </Grid>
        )}
      </Grid>

      {hasToPay && paidFull === false && <NotFullyPaidWarning />}

      <FormikTextField
        formik={formik}
        path={fieldPath("note")}
        label={t(`${section}.note`)}
        multiline
        minRows={2}
        slotProps={{ input: { inputProps: { maxLength: DependantMaxLength.note } } }}
      />
    </>
  );
}
